"use client";

import { HelpCircleIcon, Heart } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import { SignInButton } from "@/components/auth/SignInButton";
import ContactUs from "@/components/ContactUsModal";
import FaqModal from "@/components/help/FaqModal";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLocalStorage } from "@/hooks/use-local-storage";
import { useSession } from "@/lib/auth/client";
import { useListingEditorVisibility } from "@/lib/context/ListingEditorVisibilityContext";

import { ImpersonationBanner } from "../impersonation/ImpersonationBanner";

import { AvatarMenu } from "./AvatarMenu";

export function Navbar() {
  const router = useRouter();
  const { data: session, isPending } = useSession();
  const user = session?.user;
  const { isListingEditorVisible } = useListingEditorVisibility();
  const [contactOpen, setContactOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useLocalStorage<"tenant" | "business">(
    "apartmentdibs.menuStyle",
    "tenant",
  );

  // if the user has no business, ensure the menu style is tenant
  useEffect(() => {
    // TODO: Check user's organization membership once we wire up business→organization
    if (!user) {
      setMenuStyle("tenant");
    }
  }, [user, setMenuStyle]);

  // Business profile specific FAQs
  const businessFaqs = [
    {
      question: "How do I create an auction-style listing?",
      answer:
        "Go to Landlord → Create Listing. Fill out the property details, add photos and videos, set your starting price, and choose the auction duration. You can preview before publishing.",
    },
    {
      question: "Can I set a reserve price or buy-now price?",
      answer:
        "Yes. In the Pricing step of the listing form, set a reserve price to ensure you only accept bids at or above your threshold. Optionally enable a buy-now price to allow instant acceptance.",
    },
    {
      question: "How do I schedule open houses or showings?",
      answer:
        "When editing a listing, open the Scheduling section to add one-time or recurring showing windows. These will appear on the listing so bidders can plan visits.",
    },
    {
      question: "How do I screen bidders and collect applications?",
      answer:
        "Enable Screening in the listing editor to request documents and answers from bidders (income, references, etc.). You can review submissions alongside bid history.",
    },
    {
      question: "How do I accept a winning bid and finalize?",
      answer:
        "From Landlord → Listings, open the listing and go to Bids. Click Accept on the selected bid. The bidder is notified and you'll be guided through next steps (application, lease, and move-in timeline).",
    },
    {
      question: "Can I edit a listing after bids have started?",
      answer:
        "You can edit most details (media, description, showing times). Price-related fields and auction duration have restrictions once bidding begins to ensure fairness.",
    },
    {
      question: "What fees do I pay and how are payouts handled?",
      answer:
        "We charge a service fee on successful transactions. Payouts are sent to your connected account after bid acceptance and completion of the required steps.",
    },
    {
      question: "How do I end an auction early or cancel?",
      answer:
        "You can end early if no qualifying bids exist or for compliance reasons. Open the listing's actions menu → End auction. Cancellations with active bids may be restricted.",
    },
  ];

  return (
    <>
      <ImpersonationBanner className="mb-4" />
      <nav className="border-b bg-background">
        <div className="container mx-auto flex items-center justify-between px-6 py-4">
          {/* Logo */}
          <div className="group relative flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  href="/"
                  className="flex items-center gap-2 transition-opacity hover:opacity-85"
                  aria-label="ApartmentDibs home"
                >
                  {/* Mobile: mark only */}
                  <Image
                    src="/AD_logo.svg"
                    alt="ApartmentDibs"
                    width={32}
                    height={32}
                    priority
                    className="h-[22px] w-auto md:hidden dark:brightness-0 dark:invert"
                  />
                  {/* Desktop: full logo */}
                  <Image
                    src="/AD_logo.svg"
                    alt="ApartmentDibs"
                    width={160}
                    height={32}
                    priority
                    className="hidden h-[22px] w-auto md:block dark:brightness-0 dark:invert"
                  />
                </Link>
              </TooltipTrigger>
              <TooltipContent sideOffset={8}>
                <p>ApartmentDibs</p>
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Browse Button - hidden for business profile */}
            {menuStyle === "tenant" && (
              <Button variant="link" asChild aria-label="Browse listings">
                <Link href="/browse">Browse</Link>
              </Button>
            )}

            {/* Business profile: Submit Property */}
            {menuStyle === "business" && (
              <Button
                variant="link"
                aria-label="Submit property"
                onClick={() => router.push(`/landlord/listings/new`)}
                disabled={isListingEditorVisible}
              >
                Create a listing
              </Button>
            )}

            {/* Help Icon - visible for both profiles */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="link"
                  className="size-[36px] p-0"
                  onClick={() => setFaqOpen(true)}
                  aria-label="FAQ"
                >
                  <HelpCircleIcon className="size-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>More info</p>
              </TooltipContent>
            </Tooltip>

            {/* User Section */}
            {isPending && (
              <div className="flex items-center gap-3">
                <div className="size-[36px] animate-pulse rounded-full bg-secondary" />
                <div className="size-[36px] animate-pulse rounded-full bg-secondary" />
              </div>
            )}

            {user && (
              <>
                {menuStyle === "tenant" && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="link"
                        asChild
                        className="size-[36px] p-0 focus:outline-none focus-visible:outline-none"
                      >
                        <Link href="/favorites" aria-label="Go to Favorites">
                          <Heart className="size-5" />
                        </Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Favorites</p>
                    </TooltipContent>
                  </Tooltip>
                )}

                {/* Notifications */}
                {/* <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="secondary"
                      className="relative h-[40px] w-[40px] cursor-pointer rounded-full p-0"
                      aria-label={`Notifications (${notificationCount} unread)`}
                    >
                      <BellIcon className="size-5" />
                      {notificationCount > 0 && (
                        <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-medium text-white outline-2 outline-[var(--background)]">
                          {notificationCount > 9 ? "9+" : notificationCount}
                        </span>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Notifications</p>
                  </TooltipContent>
                </Tooltip> */}
                <AvatarMenu
                  onContactUsAction={() => setContactOpen(true)}
                  onMenuStyleChangeAction={setMenuStyle}
                />
              </>
            )}

            {!user && !isPending && (
              <SignInButton variant="link">Login</SignInButton>
            )}
          </div>
        </div>
      </nav>
      <FaqModal
        open={faqOpen}
        onOpenChange={setFaqOpen}
        faqs={menuStyle === "business" ? businessFaqs : undefined}
      />
      <ContactUs open={contactOpen} onOpenChange={setContactOpen} />
    </>
  );
}
